import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ref, get } from 'firebase/database';
import { db } from '../../firebase';

interface Testimonial {
  id: string;
  name: string;
  review: string;
  showInHome: boolean;
  stars?: number;
}

const TestimonialsSection: React.FC = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const testimonialsRef = ref(db, 'testimonials');
        const snapshot = await get(testimonialsRef);
        
        if (snapshot.exists()) {
          const allTestimonials = Object.entries(snapshot.val()).map(([id, data]: [string, any]) => ({
            id,
            ...data
          })) as Testimonial[];
          
          // Filter testimonials that should show on home page and limit to 3
          const homeTestimonials = allTestimonials
            .filter(testimonial => testimonial.showInHome === true)
            .slice(0, 3);
          setTestimonials(homeTestimonials);
        }
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  if (loading) {
    return (
      <Container className="apoplsp">
        <MainWrapper>
          <Header>Client <span style={{ color: '#784dcc' }}>Testimonials</span></Header>
          <LoadingMessage>Loading testimonials...</LoadingMessage>
        </MainWrapper>
      </Container>
    );
  }

  if (testimonials.length === 0) {
    return null; // Don't show section if no testimonials
  }

  return (
    <Container className="apoplsp">
      <MainWrapper>
        <Header>Client <span style={{ color: '#784dcc' }}>Testimonials</span></Header>
        <Description>
          Hear what our satisfied clients have to say about their trading experience.
        </Description>

        <TestimonialsGrid>
          {testimonials.map((testimonial) => (
            <TestimonialCard key={testimonial.id}>
              <QuoteIcon>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z" 
                    fill="#ccc"
                  />
                </svg>
              </QuoteIcon>
              <ReviewText>{testimonial.review}</ReviewText>
              <Separator />
              <StarRating>
                {[...Array(5)].map((_, index) => (
                  <Star key={index} filled={index < (testimonial.stars || 0)}>
                    ★
                  </Star>
                ))}
              </StarRating>
              <AuthorName>{testimonial.name}</AuthorName>
            </TestimonialCard>
          ))}
        </TestimonialsGrid>

        <ViewAllButton>
          <button   
            onClick={() => window.location.href = "/testimonials"}
            className="button-prim"
          >
            <div style={{
              display: "flex",
              fontSize: 16,
              justifyContent: "center",
              alignItems: "center",
              margin: 0,
              padding: 0,
            }}>
              View All Testimonials
            </div>
          </button>
        </ViewAllButton>
      </MainWrapper>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  margin-top: -30px;
  padding: 0px 20px 100px;
  background: #E7ECF9;
`;

const MainWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.h1`
  text-align: center;
  color: #000;
  font-weight: 500;
  font-size: 45px;
  font-family: var(--font-bold);
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Description = styled.p`
  text-align: center;
  font-size: 17px;
  color: #666666;
  margin: 0 auto 50px;
  max-width: 700px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 30px;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  font-size: 18px;
  color: #666;
  padding: 40px;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 40px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 30px;
  }
`;

const TestimonialCard = styled.div`
  background: #fff;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 25px;
  }
`;

const QuoteIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  opacity: 0.6;
`;

const ReviewText = styled.p`
  font-size: 16px;
  color: #555;
  line-height: 1.7;
  margin-bottom: 20px;
  font-style: italic;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const Separator = styled.div`
  width: 60px;
  height: 1px;
  background: #e0e0e0;
  margin: 0 auto 15px;
`;

const StarRating = styled.div`
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-bottom: 15px;
`;

const Star = styled.span<{ filled: boolean }>`
  font-size: 20px;
  color: ${props => props.filled ? '#FFD700' : '#e0e0e0'};
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const AuthorName = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
  font-family: var(--font-semiBold);
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ViewAllButton = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

export default TestimonialsSection;

